import { UploadError } from '@/features/angle';

/**
 * Turn an upload failure into text a user in any of the 24 locales can read.
 *
 * Only client-composed English internals are translated here: `UploadError`
 * carries a reason code instead of a message ("Network error during upload",
 * an S3 XML body, a canvas failure). Everything else is left verbatim on
 * purpose — those messages are either the server's own localized `detail`
 * (ApiClientError, e.g. the typed 413 FILE_TOO_LARGE) or copy the caller
 * already translated (page-count and size validations).
 *
 * Mirrors Android StringProvider.getLocalizedError and Apple S3Error.
 *
 * @param t translator bound to the `fileUpload` namespace.
 * @param fallback shown when the thrown value is not an Error at all.
 */
export function localizeUploadError(
  error: unknown,
  t: (key: string) => string,
  fallback: string
): string {
  if (error instanceof UploadError) {
    switch (error.reason) {
      case 'network':
        return t('uploadNetworkError');
      case 'prepare':
        return t('uploadPrepareFailed');
      default:
        return t('uploadFailedRetry');
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}
